import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Alert, 
  Badge, 
  Row, 
  Col,
  Spinner 
} from 'react-bootstrap';
import api from '../../config/api';

const ProductTypesManager = () => {
  const [productTypes, setProductTypes] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    icon_url: '',
    sort_order: 0,
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    fetchProductTypes();
    fetchSummary();
  }, []);

  const fetchProductTypes = async () => {
    try {
      const response = await api.get('/admin/product-types');
      setProductTypes(response.data);
    } catch (error) {
      showAlert('danger', 'Product types yüklenemedi!');
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/admin/product-types/summary/all');
      setSummary(response.data);
      setLoading(false);
    } catch (error) {
      showAlert('danger', 'Özet bilgiler yüklenemedi!');
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const handleShowModal = (productType = null) => {
    if (productType) {
      setEditingType(productType);
      setFormData({
        name: productType.name,
        display_name: productType.display_name,
        description: productType.description || '',
        icon_url: productType.icon_url || '',
        sort_order: productType.sort_order || 0,
        is_active: productType.is_active
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        display_name: '',
        description: '',
        icon_url: '',
        sort_order: 0,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      icon_url: '',
      sort_order: 0,
      is_active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingType) {
        // Update existing product type
        await api.put(`/admin/product-types/${editingType.id}`, formData);
        showAlert('success', 'Product type başarıyla güncellendi!');
      } else {
        // Create new product type
        await api.post('/admin/product-types', formData);
        showAlert('success', 'Product type başarıyla oluşturuldu!');
      }

      handleCloseModal();
      fetchProductTypes();
      fetchSummary();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'İşlem başarısız!';
      showAlert('danger', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productType) => {
    if (!window.confirm(`"${productType.display_name}" product type'ını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await api.delete(`/admin/product-types/${productType.id}`);
      showAlert('success', 'Product type başarıyla silindi!');
      fetchProductTypes();
      fetchSummary();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Silme işlemi başarısız!';
      showAlert('danger', errorMessage);
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <Badge bg={isActive ? 'success' : 'secondary'}>
        {isActive ? 'Aktif' : 'Pasif'}
      </Badge>
    );
  };

  const getProductTypeIcon = (name) => {
    const icons = {
      'sublimasyon': '🎨',
      'silikon': '🔧',
      'temperli_cam': '🛡️',
      'kablo_koruyucu': '🔌'
    };
    return icons[name] || '📦';
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="product-types-manager">
      {alert.show && (
        <Alert variant={alert.type} className="mb-3">
          {alert.message}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        {summary.map((item) => (
          <Col md={3} key={item.id} className="mb-3">
            <Card className="text-center h-100">
              <Card.Body>
                <div style={{ fontSize: '2rem' }}>
                  {getProductTypeIcon(item.name)}
                </div>
                <Card.Title className="h6 mt-2">{item.display_name}</Card.Title>
                <div className="d-flex justify-content-between small">
                  <span>Ürün: {item.total_products}</span>
                  <span>Renk: {item.total_color_combinations}</span>
                </div>
                {getStatusBadge(item.is_active)}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>🏷️ Product Types Yönetimi</h4>
        <Button variant="primary" onClick={() => handleShowModal()}>
          + Yeni Product Type
        </Button>
      </div>

      {/* Product Types Table */}
      <Card>
        <Card.Body>
          <Table striped bordered hover responsive className="product-types-table">
            <thead>
              <tr>
                <th width="60">Icon</th>
                <th width="120">Name</th>
                <th width="150">Display Name</th>
                <th>Description</th>
                <th width="100">Sort Order</th>
                <th width="80">Status</th>
                <th width="100">Products</th>
                <th width="100">Colors</th>
                <th width="160">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {productTypes.map((productType) => {
                const summaryItem = summary.find(s => s.id === productType.id);
                return (
                  <tr key={productType.id}>
                    <td className="text-center">
                      {productType.icon_url ? (
                        <img 
                          src={productType.icon_url} 
                          alt={productType.name}
                          style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                        />
                      ) : (
                        <span style={{ fontSize: '1.5rem' }}>
                          {getProductTypeIcon(productType.name)}
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      <code className="product-type-name">{productType.name}</code>
                    </td>
                    <td>
                      <strong className="product-type-display-name">{productType.display_name}</strong>
                    </td>
                    <td>
                      <div className="product-type-description">
                        {productType.description || '-'}
                      </div>
                    </td>
                    <td className="text-center">
                      <Badge bg="secondary">{productType.sort_order}</Badge>
                    </td>
                    <td className="text-center">
                      {getStatusBadge(productType.is_active)}
                    </td>
                    <td className="text-center">
                      <Badge bg="info">{summaryItem?.total_products || 0}</Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="warning">{summaryItem?.total_color_combinations || 0}</Badge>
                    </td>
                    <td className="text-center">
                      <div className="action-buttons d-flex justify-content-center gap-2">
                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() => handleShowModal(productType)}
                          title="Düzenle"
                          className="action-btn edit-btn"
                        >
                          <span className="button-icon">✏️</span>
                          <span className="button-text">Düzenle</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(productType)}
                          title="Sil"
                          disabled={summaryItem?.total_products > 0}
                          className="action-btn delete-btn"
                        >
                          <span className="button-icon">🗑️</span>
                          <span className="button-text">Sil</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {productTypes.length === 0 && (
            <div className="text-center text-muted p-4">
              Henüz product type eklenmemiş.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingType ? 'Product Type Düzenle' : 'Yeni Product Type'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name (sistem adı) *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="silikon_kilif"
                    required
                  />
                  <Form.Text className="text-muted">
                    Küçük harf, alt çizgi kullanın
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Name (görünen ad) *</Form.Label>
                  <Form.Control
                    type="text"
                    name="display_name"
                    value={formData.display_name}
                    onChange={handleInputChange}
                    placeholder="Silikon Kılıf"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Ürün tipi hakkında açıklama..."
              />
            </Form.Group>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="icon_url"
                    value={formData.icon_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/icon.png"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Sıralama</Form.Label>
                  <Form.Control
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                label="Aktif"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Kaydediliyor...
              </>
            ) : (
              editingType ? 'Güncelle' : 'Oluştur'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductTypesManager;
